// When "Mitä uutta" comes (Axel 2026-10-14): once per person, on the next app open after its deploy, on Tänään.
// Shown when all of these hold:
//   - the page is the real app (armed by main.tsx; never in unit tests) and the person is known from the server
//     this session (identity 'server': a fresh /me, so a tour seen on the other device is not shown again), not
//     the demo, not the login screen;
//   - the startup screens are over: the update check, the warm-up (startup-gates.ts) and the audio download
//     (AudioGate.tsx);
//   - Tänään is on the screen and no deck has been begun there (shell/tour/signals.ts: a card revealed or done);
//   - the person has not seen it: user.intros_seen on the server (any device) or this device's copy
//     (state/actions.ts introSeen; the tour's id WHATS_NEW_SEEN).
// Or a replay from Asetukset (whats-new.settings.tsx), which only waits for the startup screens and Tänään.
// Seen = Valmis, Ohita, Esc or a tap beside the caption: marked at once on this device and queued for the server
// (PATCH /api/me { introSeen }, the outbox; the demo writes nothing). After it, back to Tänään if a step moved away.
import { lazy, Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';
import { introSeen } from '../../state/actions';
import { useApp } from '../../state/store';
import { onUpdateGateChange, startupBusy } from '../startup-gates';
import { toursArmed, useTourSignals } from './signals';
import { TODAY_PATH, WHATS_NEW_SEEN, WHATS_NEW_TOUR } from './whats-new';

// the facts and the tour itself load only when it comes (the shell is in the app's first chunk)
const WhatsNewRun = lazy(() => import('./WhatsNewRun').then((m) => ({ default: m.WhatsNewRun })));

/** Whether the tour starts now (pure; WhatsNewHost reads the live values). */
export function whatsNewDue(c: {
  armed: boolean;
  replay: boolean;
  phase: 'loading' | 'ready';
  identity: string;
  needsLogin: boolean;
  startupBusy: boolean;
  audioGate: boolean;
  pathname: string;
  touched: boolean;
  seen: boolean;
}): boolean {
  if (c.phase !== 'ready' || c.needsLogin || c.startupBusy || c.audioGate || c.pathname !== TODAY_PATH) return false;
  if (c.replay) return true;
  return c.armed && c.identity === 'server' && !c.touched && !c.seen;
}

export function WhatsNewHost() {
  const { pathname } = useLocation();
  const phase = useApp((s) => s.phase);
  const identity = useApp((s) => s.identity);
  const needsLogin = useApp((s) => s.needsLogin);
  const me = useApp((s) => s.me);
  const busy = useSyncExternalStore(onUpdateGateChange, startupBusy, () => false);
  const sig = useTourSignals();
  const replay = sig.replay === WHATS_NEW_TOUR;
  // once open it stays open until closed (a step's route leaves Tänään; a sync may bring the seen flag)
  const [open, setOpen] = useState(false);
  const due = whatsNewDue({
    armed: toursArmed(),
    replay,
    phase,
    identity,
    needsLogin,
    startupBusy: busy,
    audioGate: sig.audioGate,
    pathname,
    touched: sig.touched,
    seen: introSeen(me, WHATS_NEW_SEEN),
  });
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    if (due && !open) setOpen(true);
  }, [due, open]);
  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <WhatsNewRun replay={replay} onEnd={() => setOpen(false)} />
    </Suspense>
  );
}
